/**
 * Maps Firebase Auth and Cloud Functions failures to translation keys under `errors.`.
 *
 * Firebase's own messages ("Firebase: Error (auth/invalid-credential).") and the
 * English text our Cloud Functions return must never reach the page: they are not
 * translated and mean nothing to the people using Careumba.
 */

type FirebaseLikeError = {
  code?: string;
  message?: string;
  details?: { reason?: string } | null;
};

/**
 * Machine-readable reasons our Cloud Functions put in `HttpsError.details.reason`
 * (registerUser and acceptInvite). Checked first; the English-text matching below
 * only covers servers deployed before reasons existed.
 */
const REASON_KEYS: Record<string, string> = {
  // registerUser
  'missing-fields': 'errors.missingFields',
  'invalid-role': 'errors.generic',
  'rate-limited': 'errors.tooManyRequests',
  'invite-code-required': 'errors.inviteRequired',
  'invalid-invite-code': 'errors.inviteInvalid',
  'invite-used': 'errors.inviteUsed',
  'invite-expired': 'errors.inviteExpired',
  'username-taken': 'errors.usernameTaken',
  'email-exists': 'errors.emailInUse',
  'invalid-email': 'errors.invalidEmail',
  'weak-password': 'errors.weakPassword',
  'registration-failed': 'errors.generic',
  // acceptInvite (shares the invite reasons above)
  'unauthenticated': 'errors.signInAgain',
  'own-circle': 'errors.ownCircle',
  'care-recipient-not-found': 'errors.inviteInvalid',
  'internal': 'errors.generic',
};

const AUTH_CODE_KEYS: Record<string, string> = {
  'auth/invalid-credential': 'errors.invalidCredentials',
  'auth/invalid-login-credentials': 'errors.invalidCredentials',
  'auth/wrong-password': 'errors.invalidCredentials',
  'auth/user-not-found': 'errors.invalidCredentials',
  'auth/user-disabled': 'errors.userDisabled',
  'auth/too-many-requests': 'errors.tooManyRequests',
  'auth/network-request-failed': 'errors.network',
  'auth/invalid-email': 'errors.invalidEmail',
  'auth/missing-email': 'errors.emailRequired',
  'auth/email-already-in-use': 'errors.emailInUse',
  'auth/weak-password': 'errors.weakPassword',
  'auth/missing-password': 'errors.passwordRequired',
};

/**
 * Older deployments of our functions only send English text. Recognise the known
 * sentences so those still get a translated message until every function sends a
 * `details.reason`.
 */
const LEGACY_MESSAGE_KEYS: Array<[RegExp, string]> = [
  [/username/i, 'errors.usernameTaken'],
  [/already exists/i, 'errors.emailInUse'],
  [/already been used|not pending/i, 'errors.inviteUsed'],
  [/expired/i, 'errors.inviteExpired'],
  [/invite code is required|inviteCode is required/i, 'errors.inviteRequired'],
  [/invalid invite|invite code not found|not found/i, 'errors.inviteInvalid'],
  [/valid email/i, 'errors.invalidEmail'],
  [/too weak/i, 'errors.weakPassword'],
  [/too many/i, 'errors.tooManyRequests'],
];

export function firebaseErrorKey(err: unknown): string {
  const e = (err ?? {}) as FirebaseLikeError;
  const code = typeof e.code === 'string' ? e.code : '';

  const reason = e.details && typeof e.details === 'object' ? e.details.reason : undefined;
  if (reason && REASON_KEYS[reason]) return REASON_KEYS[reason];

  if (AUTH_CODE_KEYS[code]) return AUTH_CODE_KEYS[code];

  if (code.startsWith('functions/')) {
    if (code === 'functions/resource-exhausted') return 'errors.tooManyRequests';
    if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') return 'errors.network';
    if (code === 'functions/unauthenticated') return 'errors.signInAgain';
    const message = e.message ?? '';
    for (const [pattern, key] of LEGACY_MESSAGE_KEYS) {
      if (pattern.test(message)) return key;
    }
    if (code === 'functions/not-found') return 'errors.inviteInvalid';
  }

  return 'errors.generic';
}
